/**
 * Shared `payment_type` vocabulary for the GA4 dataLayer (FR7-4279).
 *
 * KEEP IN SYNC with `7fridays-backend/src/tracking/payment-type.ts`. The table
 * has to exist twice because the two events that carry `payment_type` are
 * emitted from different places: `website_message`/`add_payment_info` here in
 * the shop, `purchase` server-side (it reaches the dataLayer via the
 * `X-Dl-Events` response header). Both sides derive the label from the very
 * same input — the Adyen payment method `type` and, for cards, the `brand` the
 * drop-in submits to `/payments` — so the same booking always reports the same
 * label in every event. Deriving it anywhere else (e.g. from Adyen's payment
 * response, which resolves the card brand behind Apple/Google Pay) would break
 * that parity.
 */

export type TrackedPaymentMethod = {
  /** Adyen txVariant, e.g. `scheme`, `klarna_paynow`, `applepay`. */
  type?: string | null
  /** Card brand detected by Adyen, e.g. `visa`, `mc`. Cards only. */
  brand?: string | null
}

/** Used when the payment method is genuinely unknown (no type submitted). */
const UNKNOWN_PAYMENT_TYPE = 'Unknown'

/**
 * Adyen txVariant → analytics label. Unmapped types fall through to the raw
 * txVariant instead of a catch-all bucket, so a newly enabled payment method
 * shows up in GA4 by its Adyen name right away rather than silently hiding in
 * 'Other' (which is what FR7-4279 is about).
 */
const PAYMENT_METHOD_LABELS: Record<string, string> = {
  // Cards
  scheme: 'Credit Card',
  card: 'Credit Card',
  storedCard: 'Credit Card',
  bcmc: 'Bancontact',
  // Wallets
  paypal: 'PayPal',
  applepay: 'Apple Pay',
  googlepay: 'Google Pay',
  // Adyen's legacy txVariant for Google Pay, still returned by some merchant
  // account configurations.
  paywithgoogle: 'Google Pay',
  amazonpay: 'Amazon Pay',
  cashapp: 'Cash App Pay',
  clicktopay: 'Click to Pay',
  // Klarna — the checkout offers these as three separate tiles and they behave
  // very differently (Pay Over Time runs a credit check), so they stay apart.
  klarna: 'Klarna - Pay Later',
  klarna_paynow: 'Klarna - Pay Now',
  klarna_account: 'Klarna - Pay Over Time',
  klarna_b2b: 'Klarna - B2B',
  // Bank transfer / open invoice
  sepadirectdebit: 'SEPA Direct Debit',
  directEbanking: 'Sofort',
  giropay: 'giropay',
  eps: 'EPS',
  ideal: 'iDEAL',
  twint: 'TWINT',
  trustly: 'Trustly',
  riverty: 'Riverty',
  ratepay: 'RatePay',
  ratepay_directdebit: 'RatePay - Direct Debit',
}

/** Adyen card brand code → analytics label. */
const CARD_BRAND_LABELS: Record<string, string> = {
  visa: 'Visa',
  mc: 'Mastercard',
  amex: 'American Express',
  maestro: 'Maestro',
  bcmc: 'Bancontact',
  diners: 'Diners Club',
  discover: 'Discover',
  jcb: 'JCB',
  cartebancaire: 'Carte Bancaire',
  cartesbancaires: 'Carte Bancaire',
  unionpay: 'UnionPay',
  uatp: 'UATP',
  hipercard: 'Hipercard',
  elo: 'Elo',
  mir: 'Mir',
}

/**
 * Builds the `payment_type` value: `'<method>'`, or `'<method> - <brand>'` when
 * Adyen resolved a card brand (e.g. `'Credit Card - Visa'`). The prefix is kept
 * so GA4 can group all card payments with a starts-with filter while still
 * breaking them down by brand.
 */
export function getPaymentTypeLabel(method?: TrackedPaymentMethod | null): string {
  const type = method?.type?.trim()
  if (!type) return UNKNOWN_PAYMENT_TYPE

  const label = PAYMENT_METHOD_LABELS[type] ?? type
  const brand = method?.brand?.trim()
  if (!brand) return label

  const brandLabel = CARD_BRAND_LABELS[brand] ?? brand

  // e.g. Bancontact submits brand === type; don't stutter.
  return brandLabel === label ? label : `${label} - ${brandLabel}`
}
